import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Youtube, Play, X, Bell, Calendar, Clock, ArrowLeft, ExternalLink, Users, Video, Mic, AlertTriangle, ChevronLeft, ChevronRight, Mail, Phone, CheckCircle, User, Instagram, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CountdownTimer = ({ targetDate }) => {
  const calc = () => {
    const diff = new Date(targetDate).getTime() - Date.now();
    if (diff <= 0) return null;
    return {
      dias: Math.floor(diff / (1000 * 60 * 60 * 24)),
      horas: Math.floor((diff / (1000 * 60 * 60)) % 24),
      min: Math.floor((diff / (1000 * 60)) % 60),
      seg: Math.floor((diff / 1000) % 60),
    };
  };

  const [time, setTime] = useState(calc());

  useEffect(() => {
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [targetDate]);

  if (!time) {
    return (
      <div className="bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3 mb-4 text-center">
        <span className="text-amber-300 font-bold text-sm">A live já começou! 🔴</span>
      </div>
    );
  }

  const Box = ({ value, label }) => (
    <div className="flex flex-col items-center bg-black/40 border border-amber-400/30 rounded-lg px-2.5 py-2 min-w-[52px]">
      <span className="text-amber-300 font-black text-xl md:text-2xl tabular-nums">{String(value).padStart(2, '0')}</span>
      <span className="text-amber-200/70 text-[10px] uppercase tracking-wide">{label}</span>
    </div>
  );

  return (
    <div className="mb-4" data-testid="launch-countdown">
      <p className="text-amber-200/80 text-xs font-semibold mb-2 text-center uppercase tracking-wide">Faltam</p>
      <div className="flex items-center justify-center gap-2">
        <Box value={time.dias} label="dias" />
        <Box value={time.horas} label="horas" />
        <Box value={time.min} label="min" />
        <Box value={time.seg} label="seg" />
      </div>
    </div>
  );
};

const BimtalksPage = () => {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [launchEvent, setLaunchEvent] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [loading, setLoading] = useState(true);
  
  // Subscription form state
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeEventId, setSubscribeEventId] = useState(null);
  const [subscribeForm, setSubscribeForm] = useState({ name: '', email: '', whatsapp: '' });
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const [eventsRes, nextRes, launchRes] = await Promise.all([
          fetch(`${API_URL}/api/bimtalks/events/all?year=2026&month=${selectedMonth + 1}`),
          fetch(`${API_URL}/api/bimtalks/events/next`),
          fetch(`${API_URL}/api/bimtalks/events/launch`)
        ]);
        
        if (eventsRes.ok) {
          const data = await eventsRes.json();
          setEvents(data);
        }
        
        if (nextRes.ok) {
          const data = await nextRes.json();
          setNextEvent(data);
        }

        if (launchRes.ok) {
          const data = await launchRes.json();
          setLaunchEvent(data);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Erro ao carregar eventos BIMTALKS:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [selectedMonth]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeForm.name || !subscribeForm.email) {
      toast.error('Preencha nome e email');
      return;
    }
    
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/bimtalks/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...subscribeForm,
          event_id: subscribeEventId
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSubscriptionSuccess(true);
        toast.success('Inscrição realizada com sucesso!');
      } else {
        toast.error(data.detail || 'Erro ao realizar inscrição');
      }
    } catch (error) {
      toast.error('Erro ao realizar inscrição');
    } finally {
      setSubscribing(false);
    }
  };

  const openSubscribeModal = (eventId) => {
    setSubscribeEventId(eventId);
    setSubscribeForm({ name: '', email: '', whatsapp: '' });
    setSubscriptionSuccess(false);
    setShowSubscribeModal(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Vídeos principais do canal
  const featuredVideos = [
    {
      id: '8WhbHf0Y_G4',
      title: 'Arquitetura em BIM',
      description: 'Aprenda os fundamentos da modelagem arquitetônica em Revit',
      category: 'Arquitetura'
    },
    {
      id: 'wLGD7y1IYnU',
      title: 'Estrutura em BIM',
      description: 'Modelagem estrutural completa com Revit Structure',
      category: 'Estruturas'
    },
    {
      id: '593mQF6Tt3s',
      title: 'Instalações MEP',
      description: 'Instalações hidráulicas e elétricas em BIM',
      category: 'MEP'
    }
  ];

  // Mais vídeos do canal BIMTALKS - Alexander Justi
  const moreVideos = [
    {
      id: 'yBhANz4zQFo',
      title: 'Compatibilização em BIM',
      description: 'Alexander Justi entrevista especialista em compatibilização de projetos BIM',
      category: 'Entrevista'
    },
    {
      id: 'd5E0tISJPx8',
      title: 'Implantação BIM no TJDFT',
      description: 'Alexander Justi entrevista Wagner Lima sobre implantação em tribunal',
      category: 'Case'
    },
    {
      id: 'jCPzkdMkwUo',
      title: 'Dynamo em Processos BIM',
      description: 'Alexander Justi entrevista Ari Monteiro sobre automação com Dynamo',
      category: 'Tecnologia'
    },
    {
      id: 'd2w5h-7M5_M',
      title: 'OpenBIM',
      description: 'Alexander Justi entrevista Carlos Dias sobre padrões abertos em BIM',
      category: 'OpenBIM'
    },
    {
      id: '45cwVm743bA',
      title: 'Drones e Tecnologias BIM',
      description: 'Alexander Justi entrevista Marcel Kater sobre integração de drones',
      category: 'Inovação'
    },
    {
      id: '5MPbCIOKqE8',
      title: 'Metodologia de Implantação BIM',
      description: 'Apresentação sobre implantação BIM em escritórios e órgãos públicos',
      category: 'Metodologia'
    }
  ];

  const VideoCard = ({ video, featured = false }) => (
    <div
      className={`group relative bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-red-500/50 transition-all duration-300 ${featured ? 'hover:scale-[1.02]' : 'hover:scale-105'}`}
      data-testid={`video-${video.id}`}
    >
      {activeVideo === video.id ? (
        <div className="relative">
          <div className="aspect-video">
            <iframe
              src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
              title={video.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <button
            onClick={() => setActiveVideo(null)}
            className="absolute top-3 right-3 bg-black/70 hover:bg-red-600 text-white p-2 rounded-full transition-colors z-10"
            data-testid="close-video-btn"
          >
            <X size={20} />
          </button>
        </div>
      ) : (
        <div
          className="cursor-pointer"
          onClick={() => setActiveVideo(video.id)}
        >
          <div className={`relative ${featured ? 'h-56 md:h-64' : 'h-48'} overflow-hidden`}>
            <img
              src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
              alt={video.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg shadow-red-600/30">
                <Play className="text-white ml-1" size={28} />
              </div>
            </div>
            <Badge className="absolute top-3 left-3 bg-red-600/90 text-white text-xs">
              {video.category}
            </Badge>
          </div>
        </div>
      )}
      <div className="p-4">
        <h3 className="text-white font-bold text-lg mb-2 group-hover:text-red-400 transition-colors">{video.title}</h3>
        <p className="text-gray-400 text-sm line-clamp-2">{video.description}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/30 via-black to-black" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-800/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={20} className="mr-2" />
            Voltar para Home
          </button>

          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4 text-sm px-4 py-1">
              <Youtube className="w-4 h-4 mr-2 inline" />
              Canal YouTube
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-black mb-4">
              <span className="text-white">AJ | </span>
              <span className="text-red-600">BIMTALKS</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-6">
              Conteúdo gratuito sobre BIM, Revit e Tecnologia para Construção
            </p>

            <div className="inline-flex items-center bg-gradient-to-r from-red-900/50 to-red-800/30 border border-red-500/30 rounded-2xl px-6 py-4 mb-8">
              <div className="flex items-center mr-6">
                <Youtube className="w-6 h-6 text-red-500 mr-2" />
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-2" />
                <span className="text-red-400 font-semibold">LIVES AO VIVO</span>
              </div>
              <div className="border-l border-red-500/30 pl-6 text-left">
                <div className="flex items-center text-white font-bold">
                  <Calendar className="w-4 h-4 mr-2 text-red-400" />
                  Todas as Quintas
                </div>
                <div className="flex items-center text-gray-300">
                  <Clock className="w-4 h-4 mr-2 text-red-400" />
                  às 20:00h (horário de Brasília)
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.youtube.com/@Bimtalks?sub_confirmation=1"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-red-600/30"
                data-testid="subscribe-youtube-btn"
              >
                <Bell className="mr-3 group-hover:animate-bounce" size={24} />
                Inscreva-se no Canal
                <ExternalLink className="ml-2" size={18} />
              </a>

              <a
                href="https://www.instagram.com/bimportal"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-500 hover:via-pink-500 hover:to-orange-400 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-pink-600/30"
                data-testid="follow-instagram-btn"
              >
                <Instagram className="mr-3 group-hover:animate-pulse" size={24} />
                Inscreva-se no Perfil
                <ExternalLink className="ml-2" size={18} />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-12">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
              <Video className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">226+</div>
              <div className="text-gray-400 text-sm">Vídeos</div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
              <Users className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">2K+</div>
              <div className="text-gray-400 text-sm">Inscritos</div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
              <Mic className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">Lives</div>
              <div className="text-gray-400 text-sm">Semanais</div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlight Cards: Launch (left) + Next Live (right) */}
      {(nextEvent || launchEvent) && (
        <section className="py-12 bg-gradient-to-b from-black to-zinc-950" data-testid="highlight-cards-section">
          <div className="container mx-auto px-4">
            <div className={`max-w-6xl mx-auto grid gap-6 ${launchEvent && nextEvent ? 'lg:grid-cols-2' : 'max-w-4xl'}`}>

              {/* Special Launch Card */}
              {launchEvent && (
                <div
                  className="bg-gradient-to-br from-amber-900/40 via-zinc-900 to-zinc-900 border-2 border-amber-400/60 rounded-3xl p-8 relative overflow-hidden shadow-[0_0_40px_-12px_rgba(251,191,36,0.4)]"
                  data-testid="launch-highlight-card"
                >
                  <div className="absolute -top-10 -right-10 w-56 h-56 bg-amber-400/15 rounded-full blur-3xl" />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                      <Badge className="bg-amber-400 text-black px-3 py-1 text-sm font-bold">
                        🚀 LANÇAMENTO
                      </Badge>
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/40 px-3 py-1 text-sm">
                        LIVE ESPECIAL
                      </Badge>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-5">
                      {/* Photo */}
                      <div className="shrink-0 mx-auto sm:mx-0">
                        <div className="w-32 h-32 rounded-2xl overflow-hidden border-2 border-amber-400/50 bg-zinc-800">
                          <img
                            src={launchEvent.guest_photo}
                            alt={launchEvent.guest_name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h2 className="text-2xl md:text-3xl font-black text-white mb-2 leading-tight">
                          {launchEvent.title}
                        </h2>
                        <p className="text-amber-300 font-semibold text-sm mb-4">
                          com {launchEvent.guest_name}
                          {launchEvent.guest_role && (
                            <span className="text-gray-400 font-normal"> · {launchEvent.guest_role}</span>
                          )}
                        </p>

                        <div className="space-y-2 mb-4 text-sm">
                          <div className="flex items-center text-gray-200">
                            <Calendar className="w-4 h-4 text-amber-400 mr-2 shrink-0" />
                            <span>{formatDate(launchEvent.date)}</span>
                          </div>
                          <div className="flex items-center text-gray-200">
                            <Clock className="w-4 h-4 text-amber-400 mr-2 shrink-0" />
                            <span>{formatTime(launchEvent.date)}h (Brasília)</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {launchEvent.description && (
                      <p className="text-gray-300 text-sm mt-2 mb-5">{launchEvent.description}</p>
                    )}

                    <CountdownTimer targetDate={launchEvent.date} />

                    <div className="bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3 mb-5 flex items-center gap-2">
                      <span className="text-2xl">🎁</span>
                      <p className="text-amber-200 text-sm font-semibold">
                        Sorteio de brindes e bolsas de estudos para os inscritos!
                      </p>
                    </div>

                    <Button
                      onClick={() => openSubscribeModal(launchEvent.id)}
                      className="w-full bg-amber-400 hover:bg-amber-300 text-black font-bold py-3"
                      data-testid="subscribe-launch-live-btn"
                    >
                      <Bell className="mr-2" size={18} />
                      Garantir minha vaga
                    </Button>
                    <p className="text-gray-500 text-xs mt-3 text-center">
                      {launchEvent.subscribers_count} pessoas já se inscreveram
                    </p>
                  </div>
                </div>
              )}

              {/* Next Live Card */}
              {nextEvent && (
                <div className="bg-gradient-to-br from-red-900/30 via-zinc-900 to-zinc-900 border-2 border-red-500/50 rounded-3xl p-8 relative overflow-hidden" data-testid="next-live-card">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/10 rounded-full blur-3xl" />

                  <div className="relative">
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                      <Badge className="bg-red-600 text-white px-3 py-1 text-sm">
                        PRÓXIMA LIVE
                      </Badge>
                      {nextEvent.is_special_time && (
                        <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-500/30 px-3 py-1 text-sm">
                          <AlertTriangle className="w-3 h-3 mr-1 inline" />
                          HORÁRIO ESPECIAL
                        </Badge>
                      )}
                    </div>

                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                      {nextEvent.title}
                    </h2>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-gray-300">
                        <User className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                        <span className="font-semibold">Convidado:</span>
                        <span className="ml-2">{nextEvent.guest_name}</span>
                        {nextEvent.guest_role && (
                          <span className="text-gray-500 ml-1">({nextEvent.guest_role})</span>
                        )}
                      </div>
                      <div className="flex items-center text-gray-300">
                        <Calendar className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                        <span>{formatDate(nextEvent.date)}</span>
                      </div>
                      <div className="flex items-center text-gray-300">
                        <Clock className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                        <span>{formatTime(nextEvent.date)}h (Brasília)</span>
                      </div>
                    </div>

                    {nextEvent.description && (
                      <p className="text-gray-400 mb-6">{nextEvent.description}</p>
                    )}

                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-6">
                      <h3 className="text-lg font-bold text-white mb-3 flex items-center">
                        <Bell className="w-5 h-5 text-red-400 mr-2" />
                        Quero ser avisado!
                      </h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Cadastre-se para receber um lembrete antes da live começar.
                      </p>
                      <Button
                        onClick={() => openSubscribeModal(nextEvent.id)}
                        className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                        data-testid="subscribe-next-live-btn"
                      >
                        <Bell className="mr-2" size={18} />
                        Inscrever-se nesta Live
                      </Button>
                      <p className="text-gray-500 text-xs mt-3 text-center">
                        {nextEvent.subscribers_count} pessoas já se inscreveram
                      </p>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </section>
      )}

      {/* Agenda Section */}
      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-purple-600/20 text-purple-400 border-purple-500/30 mb-4">Programação 2026</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Agenda de Lives
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Confira a programação completa das lives do BIMTALKS
            </p>
          </div>

          {/* Month selector */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => setSelectedMonth(m => Math.max(0, m - 1))}
              className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
              disabled={selectedMonth === 0}
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            
            <div className="bg-zinc-800 px-6 py-3 rounded-xl min-w-[200px] text-center">
              <span className="text-white font-bold text-lg">{months[selectedMonth]} 2026</span>
            </div>
            
            <button
              onClick={() => setSelectedMonth(m => Math.min(11, m + 1))}
              className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
              disabled={selectedMonth === 11}
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Events grid */}
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-400">Carregando agenda...</p>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Nenhum evento programado para este mês</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {events.map((event) => {
                  const eventDate = new Date(event.date);
                  const isPast = eventDate < new Date();
                  
                  const isConfirmed = event.guest_name && event.guest_name !== 'A definir';
                  const igUrl = event.guest_instagram
                    ? (event.guest_instagram.startsWith('http')
                        ? event.guest_instagram
                        : `https://instagram.com/${event.guest_instagram.replace('@', '')}`)
                    : null;
                  return (
                    <div
                      key={event.id}
                      className={`bg-zinc-900/50 border rounded-xl p-6 transition-all ${
                        isPast 
                          ? 'border-zinc-800 opacity-60' 
                          : 'border-zinc-700 hover:border-red-500/50'
                      }`}
                      data-testid={`event-${event.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                        <div className="flex items-center gap-4">
                          {/* Data */}
                          <div className="bg-red-600/20 rounded-xl p-3 text-center min-w-[70px]">
                            <div className="text-2xl font-bold text-red-400">{eventDate.getDate()}</div>
                            <div className="text-xs text-gray-400 uppercase">
                              {eventDate.toLocaleDateString('pt-BR', { weekday: 'short' })}
                            </div>
                            <div className="text-[10px] text-gray-500 uppercase mt-0.5">
                              {eventDate.toLocaleDateString('pt-BR', { month: 'short' })}
                            </div>
                          </div>
                          {/* Foto do convidado (espaço reservado) */}
                          <div className="w-16 h-16 rounded-full overflow-hidden bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center shrink-0">
                            {event.guest_photo ? (
                              <img src={event.guest_photo} alt={event.guest_name} className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-7 h-7 text-zinc-500" />
                            )}
                          </div>
                        </div>

                        {/* Informações do convidado */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            {isConfirmed ? (
                              <Badge className="bg-green-600/20 text-green-400 border-green-500/30 text-xs">
                                <CheckCircle className="w-3 h-3 mr-1 inline" />
                                Confirmado
                              </Badge>
                            ) : (
                              <Badge className="bg-zinc-700/40 text-gray-400 border-zinc-600 text-xs">
                                A confirmar
                              </Badge>
                            )}
                            {event.is_special_time && (
                              <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-500/30 text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1 inline" />
                                Horário especial
                              </Badge>
                            )}
                          </div>

                          <h3 className="text-lg font-bold text-white">{event.guest_name}</h3>
                          {event.title && event.title !== 'BIMTALKS Live' && (
                            <p className="text-red-400 text-sm font-medium">{event.title}</p>
                          )}
                          {event.guest_role && (
                            <p className="text-gray-400 text-sm">{event.guest_role}</p>
                          )}

                          {/* Destaque: onde a pessoa é / trabalha */}
                          {event.guest_org && (
                            <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-md bg-red-600/15 border border-red-600/30 text-red-300 text-xs font-medium">
                              <Building2 className="w-3.5 h-3.5" />
                              {event.guest_org}
                            </div>
                          )}

                          {/* Horário + redes do convidado */}
                          <div className="flex items-center flex-wrap gap-4 mt-3 text-sm">
                            <span className="text-gray-500">
                              <Clock className="w-4 h-4 inline mr-1" />
                              {formatTime(event.date)}h
                            </span>
                            {igUrl && (
                              <a href={igUrl} target="_blank" rel="noopener noreferrer" title="Instagram"
                                 className="text-gray-400 hover:text-pink-400 transition-colors">
                                <Instagram className="w-4 h-4" />
                              </a>
                            )}
                            {event.guest_email && (
                              <a href={`mailto:${event.guest_email}`} title="Email"
                                 className="text-gray-400 hover:text-red-400 transition-colors">
                                <Mail className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Ações */}
                        <div className="flex flex-col items-stretch gap-2 lg:w-52">
                          {!isPast && isConfirmed && (
                            <Button
                              onClick={() => openSubscribeModal(event.id)}
                              className="bg-red-600 hover:bg-red-700 text-white w-full"
                              size="sm"
                              data-testid={`subscribe-live-${event.id}`}
                            >
                              <Bell className="w-4 h-4 mr-2" />
                              Inscrever-se na Live
                            </Button>
                          )}
                          {!isPast && isConfirmed && (
                            <p className="text-gray-500 text-[11px] leading-snug text-center">
                              Os inscritos receberão por e-mail o link da live. A live não será aberta ao público, apenas aos inscritos.
                            </p>
                          )}
                          {!isPast && (
                            <span className="text-gray-500 text-xs text-center">
                              {event.subscribers_count} inscritos
                            </span>
                          )}
                          {isPast && event.youtube_link && (
                            <a
                              href={event.youtube_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-400 hover:text-red-300 text-sm flex items-center justify-center"
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Assistir gravação
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Videos */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4">Destaques</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Vídeos em Destaque
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Conteúdo essencial para quem quer dominar BIM e Revit
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {featuredVideos.map((video) => (
              <VideoCard key={video.id} video={video} featured />
            ))}
          </div>
        </div>
      </section>

      {/* More Videos */}
      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-zinc-800 text-gray-300 border-zinc-700 mb-4">Biblioteca</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Mais Conteúdo
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Explore nossa biblioteca completa de tutoriais e dicas
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {moreVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="https://www.youtube.com/@Bimtalks/videos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-red-400 hover:text-red-300 font-medium transition-colors"
              data-testid="view-all-videos"
            >
              Ver todos os vídeos no YouTube
              <ExternalLink className="ml-2" size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Subscribe CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-900/20 via-zinc-950 to-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-zinc-900/80 border border-red-500/20 rounded-3xl p-8 md:p-12">
              <Youtube className="w-16 h-16 text-red-600 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Não perca nenhum conteúdo!
              </h2>
              
              <p className="text-gray-300 text-lg mb-6">
                Inscreva-se no canal e ative o sininho para receber notificações de novos vídeos e das 
                <span className="text-red-400 font-semibold"> lives ao vivo todas as quintas às 20h</span>.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://www.youtube.com/@Bimtalks?sub_confirmation=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-red-600/30"
                  data-testid="subscribe-youtube-btn-bottom"
                >
                  <Bell className="mr-3 group-hover:animate-bounce" size={24} />
                  Inscrever-se Agora
                </a>
              </div>

              <p className="text-gray-500 text-sm mt-6">
                Junte-se a milhares de profissionais que já acompanham o BIMTALKS
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full relative">
            <button
              onClick={() => setShowSubscribeModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X size={24} />
            </button>
            
            {subscriptionSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Inscrição Confirmada!</h3>
                <p className="text-gray-400 mb-6">
                  Você receberá um lembrete antes da live começar.
                </p>
                <Button
                  onClick={() => setShowSubscribeModal(false)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Fechar
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">
                  <Bell className="w-6 h-6 inline mr-2 text-red-400" />
                  Inscrever-se na Live
                </h3>
                <p className="text-gray-400 mb-6">
                  Preencha seus dados para receber um lembrete
                </p>
                
                <form onSubmit={handleSubscribe} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Nome *</label>
                    <Input
                      type="text"
                      placeholder="Seu nome"
                      value={subscribeForm.name}
                      onChange={(e) => setSubscribeForm(f => ({ ...f, name: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={subscribeForm.email}
                      onChange={(e) => setSubscribeForm(f => ({ ...f, email: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">WhatsApp (opcional)</label>
                    <Input
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={subscribeForm.whatsapp}
                      onChange={(e) => setSubscribeForm(f => ({ ...f, whatsapp: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Para receber lembrete via WhatsApp</p>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={subscribing}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                  >
                    {subscribing ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                        Inscrevendo...
                      </>
                    ) : (
                      <>
                        <Bell className="mr-2" size={18} />
                        Confirmar Inscrição
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BimtalksPage;
